import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, useMotionTemplate } from 'motion/react';
import { ShieldCheck, Zap, Siren, Clock, Activity, HardHat, Compass } from 'lucide-react';

function useLightMode() {
  const [isLightMode, setIsLightMode] = useState(() => 
    typeof document !== 'undefined' && document.documentElement.classList.contains('light-mode')
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const observer = new MutationObserver(() => {
      setIsLightMode(document.documentElement.classList.contains('light-mode'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isLightMode;
}

export default function HeroInteractive() {
  const isLightMode = useLightMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeMessage, setActiveMessage] = useState(0);
  const [seconds, setSeconds] = useState(72 * 3600); // 72 horas en segundos

  // Físicas interactivas para Perspectiva 3D
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-400, 400], [12, -12]);
  const rotateY = useTransform(x, [-400, 400], [-12, 12]);

  // Reflector de mouse para efecto de iluminación
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      x.set(event.clientX - centerX);
      y.set(event.clientY - centerY);
      mouseX.set(event.clientX - rect.left);
      mouseY.set(event.clientY - rect.top);
    }
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  // Detectar idioma
  const isEn = typeof document !== 'undefined' && document.documentElement.lang === 'en';

  // Rotar los mensajes del cliente con efecto táctico
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMessage((prev) => (prev + 1) % messages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Simulación de cuenta regresiva en tiempo real (milisegundos simulados)
  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 0) return 72 * 3600;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Formatear segundos a formato HH:MM:SS
  const formatTime = () => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const messages = [
    {
      title: isEn ? "Operational Urgency" : "Urgencia Operacional",
      badge: isEn ? "IMMEDIATE 72H RESPONSE" : "RESPUESTA INMEDIATA 72H",
      text: isEn ? "The best solution within hours..." : "La mejor solución en cuestión de horas...",
      subtext: isEn 
        ? "Effective solution to operational emergency within 72 hours." 
        : "Solución Eficaz de la Urgencia Operacional en 72 hrs.",
      gradient: "from-[#FF6B00] via-[#FF8A00] to-[#FFD700]"
    },
    {
      title: isEn ? "Critical Commitment" : "Compromiso Crítico",
      badge: isEn ? "UNINTERRUPTED OPERATION" : "OPERATIVIDAD ININTERRUMPIDA",
      text: isEn ? "While others sleep..." : "Mientras otros duermen...",
      subtext: isEn 
        ? "We fully resolve the contingency in 72 Hours." 
        : "Solucionamos de manera integral la contingencia en 72 Hrs.",
      gradient: "from-[#00F0FF] via-[#007AFF] to-[#7000FF]"
    },
    {
      title: isEn ? "Loss Mitigation" : "Mitigación",
      badge: isEn ? "LOSS MITIGATION" : "MITIGACIÓN DE PÉRDIDAS",
      text: isEn ? "Every hour of downtime costs millions?" : "¿Cada hora de inactividad cuesta millones?",
      subtext: isEn 
        ? "We stop the impact and operational cost of poor decisions." 
        : "Frenamos el impacto y el costo operativo de las malas decisiones.",
      gradient: "from-[#FF0055] via-[#FF0088] to-[#FF0055]"
    },
    {
      title: isEn ? "Technical Authority" : "Autoridad",
      badge: isEn ? "TECHNICAL AUTHORITY" : "AUTORIDAD TÉCNICA",
      text: isEn 
        ? "Do not leave emergencies and contingencies in inexperienced hands." 
        : "Las emergencias y contingencias no las dejes en manos inexpertas.",
      subtext: isEn 
        ? "Our precision engineering restores your plant and piping systems in record time." 
        : "Nuestra ingenieria de precision restaura su planta y/o sistema de tuberias en tiempo record",
      gradient: "from-[#00C853] via-[#64DD17] to-[#AEEA00]"
    }
  ];

  return (
    <motion.div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      className="relative w-full max-w-[500px] min-h-[640px] mx-auto perspective-[1500px] cursor-crosshair group flex items-center justify-center"
    >
      {/* Glow externo de alto impacto */}
      <div className="absolute -inset-4 bg-gradient-to-r from-[#FF6B00]/10 via-[#007AFF]/10 to-transparent blur-[30px] rounded-[2rem] pointer-events-none opacity-80 group-hover:opacity-100 transition-opacity duration-500 -z-10" />

      {/* Contenedor HUD Holográfico Principal */}
      <div className={`w-full h-full ${isLightMode ? 'bg-[#ffffff] border-slate-200/90 shadow-[0_20px_50px_rgba(0,122,255,0.05)]' : 'bg-[#071324]/85 border-white/10 shadow-[0_20px_50px_rgba(0,122,255,0.15)]'} backdrop-blur-2xl border rounded-[2.5rem] p-6 md:p-8 overflow-hidden flex flex-col justify-between relative group-hover:border-[#FF6B00]/30 transition-all duration-500`}>
        
        {/* Linterna de luz de cursor */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 mix-blend-screen transition-opacity duration-500 pointer-events-none z-0"
          style={{
            background: useMotionTemplate`radial-gradient(350px circle at ${mouseX}px ${mouseY}px, ${isLightMode ? 'rgba(255,107,0,0.04)' : 'rgba(255,107,0,0.08)'}, transparent 50%)`
          }}
        />

        {/* HUD: Cuadrícula Técnica y Esquinas */}
        <div className={`absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none ${isLightMode ? 'opacity-20' : 'opacity-40'}`} />
        
        {/* L-Corners HUD decorativas */}
        <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-[#FF6B00]/30 group-hover:border-[#FF6B00]/70 transition-colors" />
        <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-[#FF6B00]/30 group-hover:border-[#FF6B00]/70 transition-colors" />
        <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-[#FF6B00]/30 group-hover:border-[#FF6B00]/70 transition-colors" />
        <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-[#FF6B00]/30 group-hover:border-[#FF6B00]/70 transition-colors" />

        {/* ── CABECERA HUD ── */}
        <div className={`relative z-10 flex items-center justify-between border-b ${isLightMode ? 'border-slate-100' : 'border-white/5'} pb-4`}>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#FF6B00] animate-ping" />
          </div>
          <div className={`flex items-center gap-1.5 ${isLightMode ? 'bg-[#007AFF]/8 border-[#007AFF]/25' : 'bg-[#007AFF]/10 border border-[#007AFF]/30'} px-2.5 py-0.5 rounded-full`}>
            <Activity className={`w-3.5 h-3.5 ${isLightMode ? 'text-[#007AFF]' : 'text-cyan-400'} animate-pulse`} />
            <span className={`text-[9px] font-black ${isLightMode ? 'text-[#0056b3]' : 'text-cyan-300'} uppercase tracking-wider`}>ONLINE</span>
          </div>
        </div>

        {/* ── NÚCLEO INTERACTIVO: CRONÓMETRO Y RADAR ── */}
        <div className="relative z-10 flex flex-col items-center justify-center flex-1 my-2" style={{ transform: "translateZ(60px)" }}>
          
          {/* TEXTO DE IMPACTO SUPERIOR */}
          <div className="relative w-full h-24 md:h-28 flex flex-col justify-end items-center text-center mb-6">
            {messages.map((msg, index) => (
              <motion.div
                key={`top-${index}`}
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{
                  opacity: activeMessage === index ? 1 : 0,
                  y: activeMessage === index ? 0 : -10,
                  scale: activeMessage === index ? 1 : 0.95,
                  pointerEvents: activeMessage === index ? "auto" : "none"
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{ willChange: "transform, opacity" }}
                className="absolute inset-0 flex flex-col items-center justify-end p-2"
              >
                <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-black tracking-widest ${isLightMode ? 'bg-[#007AFF]/8 border-[#007AFF]/25 text-[#0056b3] shadow-[0_0_15px_rgba(0,122,255,0.05)]' : 'bg-[#007AFF]/15 border border-[#007AFF]/40 text-white shadow-[0_0_15px_rgba(0,122,255,0.2)]'} uppercase mb-3`}>
                  {msg.badge}
                </span>
                <h3 className={`text-lg md:text-xl font-bold ${isLightMode ? 'text-slate-800' : 'text-gray-200'} leading-snug px-2`}>
                  {msg.text}
                </h3>
              </motion.div>
            ))}
          </div>

          {/* Radar circular con cronómetro en el centro */}
          <div className="relative w-56 h-56 md:w-64 md:h-64 flex items-center justify-center my-2 shrink-0">
            
            {/* Círculo exterior giratorio */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className={`absolute inset-0 rounded-full border border-dashed m-1 ${isLightMode ? 'border-[#007AFF]/20' : 'border-[#007AFF]/30'}`}
            />
            {/* Conic Gradient Sweep */}
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-2 rounded-full pointer-events-none opacity-30"
              style={{
                background: "conic-gradient(from 0deg, transparent 70%, rgba(255,107,0,0.5) 95%, rgba(255,107,0,0.9) 100%)",
                maskImage: "radial-gradient(transparent 55%, black 60%)",
                WebkitMaskImage: "radial-gradient(transparent 55%, black 60%)",
              }}
            />

            {/* Círculo central oscuro con la información de tiempo */}
            <div className={`absolute inset-4 md:inset-5 rounded-full ${isLightMode ? 'bg-slate-50 border-slate-200/80 shadow-[0_0_30px_rgba(0,122,255,0.04)]' : 'bg-[#050D19]/90 border-white/10 shadow-[0_0_30px_rgba(0,122,255,0.15)]'} flex flex-col items-center justify-center group-hover:shadow-[0_0_50px_rgba(255,107,0,0.2)] transition-shadow duration-500 z-10`}>
              <Clock className="w-6 h-6 text-[#FF6B00] mb-2 animate-pulse" />
              
              <span className={`text-[10px] md:text-xs ${isLightMode ? 'text-[#0056b3]' : 'text-cyan-400'} font-bold uppercase tracking-widest mb-1 md:mb-2 text-center leading-tight`}>
                {isEn ? "RESPONSE METRIC" : "MÉTRICA DE RESPUESTA"}
              </span>
              
              <div className={`text-3xl md:text-5xl font-black ${isLightMode ? 'text-[#0f172a]' : 'text-white'} font-mono tracking-widest leading-none drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]`}>
                {formatTime()}
              </div>

              <span className={`text-[10px] font-bold ${isLightMode ? 'text-slate-500' : 'text-gray-400'} tracking-wider uppercase mt-2`}>
                {isEn ? "TIME LIMIT" : "TIEMPO LÍMITE"}
              </span>
            </div>
            
            {/* Blips de radar de contingencia (Ductos simulados) */}
            <div className="absolute top-10 left-12 w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_10px_#00F0FF] z-20" />
            <div className="absolute bottom-12 right-10 w-3 h-3 rounded-full bg-red-500 animate-ping shadow-[0_0_12px_#EF4444] z-20" />
          </div>

          {/* TEXTO DE DOLOR INFERIOR */}
          <div className="relative w-full h-28 md:h-32 flex flex-col justify-start text-center mt-6">
            {messages.map((msg, index) => (
              <motion.div
                key={`bottom-${index}`}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{
                  opacity: activeMessage === index ? 1 : 0,
                  y: activeMessage === index ? 0 : 10,
                  scale: activeMessage === index ? 1 : 0.95,
                  pointerEvents: activeMessage === index ? "auto" : "none"
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{ willChange: "transform, opacity" }}
                className="absolute inset-0 flex flex-col items-center justify-start p-2"
              >
                <p className={`text-xl md:text-[1.7rem] font-black text-transparent bg-clip-text bg-gradient-to-r ${msg.gradient} leading-tight tracking-tight text-center px-2`}>
                  {msg.subtext}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── PIE DE TARJETA CON TELEMETRÍA ── */}
        <div className={`relative z-10 border-t ${isLightMode ? 'border-slate-100' : 'border-white/5'} pt-4 mt-auto flex items-center justify-between text-[10px] font-mono text-gray-500`}>
          <div className="flex items-center gap-2">
            <HardHat className="w-4 h-4 text-[#FF6B00]" />
            <span className="font-bold tracking-widest">{isEn ? "PLUGGING & HOT TAPPING" : "OBTURACIÓN & HOT TAPPING"}</span>
          </div>
        </div>

      </div>
    </motion.div>
  );
}
